/**
 * The browser toolbar's recording control.
 *
 * Adapted from t3code (`PreviewChromeRow`'s recording control and
 * `browserRecordingUpload`, MIT, pin `e3e7cc3f`). Forge delta: stopping
 * attaches the clip to the composer draft as a file, where upstream uploads
 * it from the desktop main process. The thread the clip attaches to comes
 * from the session snapshot, so no panel wiring carries it here.
 *
 * @module BrowserRecordButton
 */
import type { BrowserSessionSnapshot, BrowserTabSnapshot } from "@cmmd-forge/contracts";
import { Circle, CircleStop } from "lucide-react";
import { useState } from "react";

import {
  isBrowserRecordingSupported,
  startBrowserRecording,
  stopBrowserRecording,
  useBrowserRecordingStore,
} from "~/browser/browserRecording";
import { useComposerDraftStore, type ComposerFileAttachment } from "~/composerDraftStore";
import { toastManager } from "~/components/ui/toast";
import { randomUUID } from "~/lib/utils";

import { BrowserChromeIconButton } from "./BrowserChromeButton";

export interface BrowserRecordButtonProps {
  readonly session: BrowserSessionSnapshot | null;
  readonly activeTab: BrowserTabSnapshot | null;
}

export function BrowserRecordButton({ session, activeTab }: BrowserRecordButtonProps) {
  const tabId = activeTab?.tabId ?? null;
  const recording = useBrowserRecordingStore((state) =>
    tabId !== null ? Boolean(state.byTabId[tabId]) : false,
  );
  const [busy, setBusy] = useState(false);
  const disabled = tabId === null || session === null || busy || !isBrowserRecordingSupported();

  const handleToggle = () => {
    if (tabId === null || session === null || busy) return;
    setBusy(true);
    const threadRef = { environmentId: session.environmentId, threadId: session.threadId };
    void (
      recording
        ? stopBrowserRecording({ tabId }).then(async (result) => {
            if (!result) return;
            const fileAttachment: ComposerFileAttachment = {
              id: randomUUID(),
              name: result.file.name,
              mimeType: result.file.type || "video/webm",
              sizeBytes: result.file.size,
              type: "file",
              file: result.file,
            };
            useComposerDraftStore.getState().addFiles(threadRef, [fileAttachment]);
            toastManager.add({
              type: "success",
              title: "Recording attached",
              description: "The browser recording was added to the composer.",
            });
          })
        : startBrowserRecording({ tabId })
    )
      .catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Browser recording failed",
          description:
            error instanceof Error ? error.message : "The browser could not be recorded.",
        });
      })
      .finally(() => setBusy(false));
  };

  return (
    <BrowserChromeIconButton
      label={recording ? "Stop recording browser preview" : "Record browser preview"}
      icon={
        recording ? (
          <CircleStop className="text-destructive" />
        ) : (
          <Circle className="size-3 fill-destructive text-destructive" />
        )
      }
      onClick={handleToggle}
      disabled={disabled}
      pressed={recording}
    />
  );
}
